import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useProjectStore,
  type ProjectSummary,
  type SharedProjectSummary,
} from '../stores/projectStore';
import { useAuthStore } from '../stores/platformAuthStore';
import { theme } from '../theme';

const styles = {
  card: {
    background: theme.bgPanel,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.border}`,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  name: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.textPrimary,
  },
  slug: {
    fontSize: '12px',
    color: theme.textMuted,
    marginTop: '2px',
  },
  badge: (status: string) => ({
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600,
    background:
      status === 'running'
        ? theme.successBg
        : status === 'starting' || status === 'restarting'
          ? theme.warningBg
          : status === 'error'
            ? theme.errorBg
            : theme.bgSurface,
    color:
      status === 'running'
        ? theme.success
        : status === 'starting' || status === 'restarting'
          ? theme.warning
          : status === 'error'
            ? theme.error
            : theme.textMuted,
  }),
  meta: {
    fontSize: '12px',
    color: theme.textMuted,
  },
  startError: {
    fontSize: '12px',
    color: theme.error,
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
  btn: {
    padding: '6px 14px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    background: theme.bgInput,
    color: theme.textSecondary,
  },
  btnPrimary: {
    padding: '6px 14px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    background: theme.accent,
    color: theme.accentText,
  },
  btnDanger: {
    padding: '6px 14px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    background: theme.danger,
    color: theme.accentText,
  },
};

export function ProjectCard({ project }: { project: ProjectSummary | SharedProjectSummary }) {
  const navigate = useNavigate();
  const { startProject, stopProject, startErrors } = useProjectStore();
  const { token } = useAuthStore();
  const [busy, setBusy] = useState(false);

  const handleStart = async () => {
    setBusy(true);
    try {
      await startProject(project.id);
    } catch {
      // error is in the store
    }
    setBusy(false);
  };

  const handleStop = async () => {
    setBusy(true);
    try {
      await stopProject(project.id);
    } catch {
      // error is in the store
    }
    setBusy(false);
  };

  const isRunning = project.status === 'running';
  const isStarting = project.status === 'starting';

  const clientBase =
    import.meta.env.VITE_CLIENT_URL ?? (import.meta.env.DEV ? 'http://localhost:5173' : '');
  const editorBase =
    import.meta.env.VITE_EDITOR_URL ?? (import.meta.env.DEV ? 'http://localhost:5174' : '');
  const playUrl = `${clientBase}/play/${project.slug}`;
  // The account token only needs to ride in the URL when the editor is on a
  // different origin (cross-origin dev), where it can't read the dashboard's
  // localStorage. In same-origin production the editor reads platform_token
  // directly, so keeping the token out of the URL keeps it out of Caddy's
  // access log and browser history.
  const editorCrossOrigin = editorBase !== '' && !editorBase.startsWith(window.location.origin);
  const editorUrl = `${editorBase}/editor/${project.slug}${
    token && editorCrossOrigin ? `?token=${encodeURIComponent(token)}` : ''
  }`;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.name}>{project.name}</div>
          <div style={styles.slug}>
            {project.slug}
            {'ownerUsername' in project ? ` | owned by ${project.ownerUsername}` : ''}
          </div>
        </div>
        <span style={styles.badge(project.status)}>{project.status}</span>
      </div>

      <div style={styles.meta}>
        {project.plan} plan | Up to {project.maxPlayers} players
        {project.port ? ` | Port ${project.port}` : ''}
      </div>

      <div style={styles.actions}>
        {isRunning ? (
          <>
            <button style={styles.btnDanger} onClick={handleStop} disabled={busy}>
              {busy ? 'Stopping...' : 'Stop'}
            </button>
            <a
              href={playUrl}
              target="_blank"
              rel="noopener"
              style={{ ...styles.btnPrimary, textDecoration: 'none', display: 'inline-block' }}
            >
              Play
            </a>
            <a
              href={editorUrl}
              target="_blank"
              rel="noopener"
              style={{ ...styles.btn, textDecoration: 'none', display: 'inline-block' }}
            >
              Editor
            </a>
          </>
        ) : (
          <button style={styles.btnPrimary} onClick={handleStart} disabled={busy || isStarting}>
            {busy || isStarting ? 'Starting...' : 'Start'}
          </button>
        )}
        <button style={styles.btn} onClick={() => navigate(`/projects/${project.id}/settings`)}>
          Settings
        </button>
      </div>
      {!isRunning && startErrors[project.id] && (
        <div style={styles.startError}>{startErrors[project.id]}</div>
      )}
    </div>
  );
}
